/**
 * Live MFA status from Keycloak (+ durable UI cache).
 * Secrets are never returned — only type, label, createdDate.
 *
 * Source of truth for enrollment: Keycloak user credential store (IdP DB).
 * Browser session/localStorage is only a UI cache for card highlighting.
 */

import { getAuthConfig, isOidcConfigured } from './config'
import { decodeClaims, getAccessToken, isDemoSession } from './oidc'

const KEY_CACHE = 'w7_mfa_status_cache'
const KEY_LOCAL = 'w7_mfa_active_methods'

/** UI cards that all map to Keycloak's single `webauthn` credential type. */
export const WEBAUTHN_METHOD_IDS = ['passkey', 'fingerprint', 'security-key'] as const

/** Keycloak cannot tell fingerprint vs USB key apart — Active lives on `passkey` only. */
export function canonicalMfaMethodId(methodId: string): string {
  return (WEBAUTHN_METHOD_IDS as readonly string[]).includes(methodId) ? 'passkey' : methodId
}

function normalizeActiveIds(ids: string[]): string[] {
  const set = new Set(ids)
  const hasWebAuthn = WEBAUTHN_METHOD_IDS.some((id) => set.has(id))
  if (hasWebAuthn) {
    for (const id of WEBAUTHN_METHOD_IDS) set.delete(id)
    set.add('passkey')
  }
  return [...set]
}

export interface MfaCredentialInfo {
  id: string
  type: string
  userLabel?: string
  createdDate?: number
}

export interface MfaStatus {
  /** Method ids from mfaMethods that have at least one credential. */
  activeMethodIds: string[]
  credentials: MfaCredentialInfo[]
  /** true when credentials API answered with JSON. */
  fromAccountApi: boolean
  error?: string
}

interface CredentialContainer {
  type?: string
  category?: string
  createAction?: string
  userCredentials?: Array<{
    id?: string
    type?: string
    userLabel?: string
    createdDate?: number
  }>
}

function accountCredentialsUrl(): string | null {
  const cfg = getAuthConfig()
  if (!isOidcConfigured(cfg)) return null
  if (import.meta.env.DEV) return '/api/account/credentials'
  return `${cfg.keycloakUrl}/realms/${cfg.realm}/account/credentials`
}

function storageKeyForUser(): string {
  const who = decodeClaims(getAccessToken())
  const id = who?.sub || who?.preferredUsername || 'anon'
  return `${KEY_LOCAL}:${id}`
}

/** Map Keycloak credential type → our method card ids (Active chips). */
export function methodIdsForCredentialType(type: string): string[] {
  const t = type.toLowerCase()
  if (t === 'otp' || t === 'totp' || t === 'hotp') return ['totp']
  // One WebAuthn credential must not light up three separate "Active" cards.
  if (t === 'webauthn' || t === 'webauthn-passwordless') return ['passkey']
  if (t.includes('recovery')) return ['recovery']
  return []
}

function readSessionCache(): MfaStatus | null {
  try {
    const raw = sessionStorage.getItem(KEY_CACHE)
    if (!raw) return null
    return JSON.parse(raw) as MfaStatus
  } catch {
    return null
  }
}

function writeSessionCache(status: MfaStatus): void {
  sessionStorage.setItem(KEY_CACHE, JSON.stringify(status))
}

function readLocalActiveIds(): string[] {
  try {
    if (typeof localStorage === 'undefined') return []
    const raw = localStorage.getItem(storageKeyForUser())
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    const ids = Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
    return normalizeActiveIds(ids)
  } catch {
    return []
  }
}

function writeLocalActiveIds(ids: string[]): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(storageKeyForUser(), JSON.stringify(normalizeActiveIds(ids)))
  } catch {
    /* private mode / missing localStorage */
  }
}

function mergeActive(apiIds: string[], localIds: string[]): string[] {
  return normalizeActiveIds([...apiIds, ...localIds])
}

/** After AIA return — mark a method active immediately for card highlight. */
export function markMethodEnrolled(methodId: string): void {
  const canonical = canonicalMfaMethodId(methodId)
  const local = readLocalActiveIds()
  writeLocalActiveIds([...local, canonical])

  const prev = readSessionCache() ?? {
    activeMethodIds: [],
    credentials: [],
    fromAccountApi: false,
  }
  prev.activeMethodIds = mergeActive(prev.activeMethodIds, [canonical, ...local])
  const label =
    methodId === 'totp'
      ? 'Authenticator app'
      : methodId === 'recovery'
        ? 'Recovery codes'
        : methodId === 'fingerprint'
          ? 'Fingerprint / device'
          : methodId === 'security-key'
            ? 'Security key'
            : 'Passkey'
  const type =
    canonical === 'totp' ? 'otp' : canonical === 'recovery' ? 'recovery-authn-codes' : 'webauthn'
  prev.credentials = [
    ...prev.credentials.filter((c) => !methodIdsForCredentialType(c.type).includes(canonical)),
    {
      id: `local-${canonical}`,
      type,
      userLabel: label,
      createdDate: Date.now(),
    },
  ]
  writeSessionCache(prev)
}

function parseContainers(containers: CredentialContainer[]): {
  credentials: MfaCredentialInfo[]
  activeMethodIds: string[]
} {
  const credentials: MfaCredentialInfo[] = []
  const active = new Set<string>()
  for (const box of containers ?? []) {
    for (const uc of box.userCredentials ?? []) {
      const type = (uc.type || box.type || '').toLowerCase()
      // Never surface password (or other non-MFA) credentials on the security UI.
      if (!type || type === 'password' || type === 'password-history') continue
      credentials.push({
        id: uc.id ?? `${type}-${credentials.length}`,
        type,
        userLabel: uc.userLabel || undefined,
        createdDate: uc.createdDate,
      })
      for (const mid of methodIdsForCredentialType(type)) active.add(mid)
    }
  }
  return { credentials, activeMethodIds: [...active] }
}

export async function fetchMfaStatus(): Promise<MfaStatus> {
  if (isDemoSession()) {
    return { activeMethodIds: [], credentials: [], fromAccountApi: false, error: 'demo' }
  }
  const localIds = readLocalActiveIds()
  const token = getAccessToken()
  const url = accountCredentialsUrl()
  if (!token || !url) {
    const cached = readSessionCache()
    return {
      activeMethodIds: mergeActive(cached?.activeMethodIds ?? [], localIds),
      credentials: cached?.credentials ?? [],
      fromAccountApi: false,
      error: 'no-session',
    }
  }

  try {
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    })
    const text = await res.text()
    const looksJson = text.trim().startsWith('[') || text.trim().startsWith('{')
    if (!res.ok || !looksJson) {
      const cached = readSessionCache()
      return {
        activeMethodIds: mergeActive(cached?.activeMethodIds ?? [], localIds),
        credentials: cached?.credentials ?? [],
        fromAccountApi: false,
        error: `account-api-${res.status}`,
      }
    }
    const containers = JSON.parse(text) as CredentialContainer[]
    const parsed = parseContainers(containers)
    // Persist API actives into local so highlights survive reload.
    writeLocalActiveIds(mergeActive(parsed.activeMethodIds, localIds))
    const status: MfaStatus = {
      activeMethodIds: mergeActive(parsed.activeMethodIds, localIds),
      credentials:
        parsed.credentials.length > 0
          ? parsed.credentials
          : (readSessionCache()?.credentials ?? []),
      fromAccountApi: true,
    }
    writeSessionCache(status)
    return status
  } catch {
    const cached = readSessionCache()
    return {
      activeMethodIds: mergeActive(cached?.activeMethodIds ?? [], localIds),
      credentials: cached?.credentials ?? [],
      fromAccountApi: false,
      error: 'network',
    }
  }
}
